import React, { useMemo, useRef } from 'react';
import { Block, Flex, Text, Link, Tooltip, Icon, Checkbox, Card } from '@upsales/components';
import ProspectingClient from 'App/resources/Model/ProspectingClient';
import SoliditetClient from 'App/resources/Model/SoliditetClient';
import BranchOffices from '../../BranchOffices';
import { CompanyRowAdd } from '../../CompanyRow';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { useAddSelector, useAddDispatch } from '../context/AddContext';
import { deselectAll, handleBranchOfficesChange, selectClient, selectAllCompanies, deselectAllCompanies } from '../context/Helpers';

import './Tabs.scss';

export enum TABS {
	ALL_COMPANIES = 'all',
	SELECTED_COMPANIES = 'selected'
}

type TabsProps = {
	unknowns: ProspectingClient[] | SoliditetClient[];
	numberOfSelectedCompanies: number;
	pendingPurchase: boolean;
	createAccount: () => void;
};

const EmptyState = ({
	mainQuery,
	subQuery,
	link,
	classes
}: {
	mainQuery?: string;
	subQuery?: string;
	link?: JSX.Element;
	classes: BemClass;
}) => {
	if (!mainQuery && !subQuery && !link) {
		return null;
	}

	return (
		<Flex className={classes.elem('emptyState').b()} justifyContent="center">
			<Flex direction="column" alignItems="center" gap="u3">
				<img src="img/empty-briefcase.svg" />
				<Text bold size="md">
					{mainQuery}
				</Text>
				<Text color="grey-11">
					<Text color="grey-11">{subQuery}</Text>
					<Text>{link}</Text>
				</Text>
			</Flex>
		</Flex>
	);
};

const Tabs = ({ unknowns, numberOfSelectedCompanies, pendingPurchase, createAccount }: TabsProps) => {
	const classes = new BemClass('SelectSubaccountTabs');
	const { t } = useTranslation();
	const dispatch = useAddDispatch();

	const {
		activeTab,
		selectableCompanies,
		nonSelectableCompanies,
		filteredBranchOffices,
		selectedBranches,
		selectedClients,
		searchQuery
	} = useAddSelector(state => state);

	const prevSelectedBranches = useRef(selectedBranches);

	const handleBranchOfficesChangeWrapper = (
		branches: ProspectingClient[] | SoliditetClient[],
		filtered?: ProspectingClient[] | SoliditetClient[]
	) => {
		dispatch(handleBranchOfficesChange(branches, prevSelectedBranches.current, filtered));

		// Update the ref after dispatch
		prevSelectedBranches.current = branches;
	};

	const filteredSelectedClients = useMemo(() => {
		return selectedClients.filter(
			client => !searchQuery.trim() || client.name.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}, [selectedClients, searchQuery]);

	switch (activeTab) {
		case TABS.ALL_COMPANIES: {
			const showEmptyState =
				!selectableCompanies.length && !nonSelectableCompanies.length && !filteredBranchOffices.length;

			const mainQuery =
				!filteredBranchOffices.length && !searchQuery
					? t('account.relations.noBranchOffices')
					: t('default.noResultsFor') + ` "${searchQuery}"`;
			const link =
				!filteredBranchOffices.length && !searchQuery ? (
					<Flex gap="u1">
						<Text>{t('account.relations.searchOr')}</Text>
						<Link onClick={() => createAccount()}>{t('default.createAccountManually').toLowerCase()}</Link>
					</Flex>
				) : (
					<Link onClick={() => createAccount()}>{t('account.relations.createAsNew')}</Link>
				);

			const allSelectableSelected = selectableCompanies.every(company => 
				selectedClients.some(c => c.id === company.id)
			);
			const someSelectableSelected = selectableCompanies.some(company => 
				selectedClients.some(c => c.id === company.id)
			);

			const toggleAllSelectable = () => {
				if (allSelectableSelected) {
					dispatch(deselectAllCompanies(selectableCompanies));
				} else {
					dispatch(selectAllCompanies(selectableCompanies));
				}
			};

			return (
				<Block>
					{unknowns?.length ? (
						<BranchOffices
							hasPendingPurchase={pendingPurchase}
							unknowns={unknowns}
							deselectAll={() => dispatch(deselectAll())}
							onChange={handleBranchOfficesChangeWrapper}
						/>
					) : null}
					<Block>
						{selectableCompanies?.length ? (
							<Block
								className={classes.elem('selectableCompanies').b()}
								border="ts"
								borderColor="grey-4"
								backgroundColor="grey-1"
							>
								<Card space="pll ptm pbm" border="bm" borderColor="grey-4">
									<Flex alignItems="center" space="mls" gap="u4">
										<Checkbox
											size="xs"
											checked={allSelectableSelected}
											half={someSelectableSelected && !allSelectableSelected}
											onClick={toggleAllSelectable}
										/>
										<Text size="sm">
											{searchQuery 
												? t('default.selectAll')
												: t('account.relations.selectableCompanies')}
										</Text>
									</Flex>
								</Card>
								{selectableCompanies.map(client => (
									<CompanyRowAdd
										key={client.id}
										company={client}
										selected={selectedClients.some(c => c.id === client.id)}
										onSelectCompany={() => dispatch(selectClient(client))}
									/>
								))}
							</Block>
						) : null}
						{nonSelectableCompanies?.length ? (
							<Block border="ts" borderColor="grey-4" backgroundColor="grey-1">
								<Flex justifyContent="space-between" alignItems="center">
									<Flex className={classes.elem('columnTitleColumn').b()} alignItems="center">
										<Text bold space="mbl mtl mll" size="sm">
											{t('account.relations.nonSelectableCompanies')}
										</Text>
									</Flex>
									<Flex space="mrl">
										<Tooltip title={t('account.relations.cantRelateTooltip')}>
											<Icon name="question-circle" />
										</Tooltip>
									</Flex>
								</Flex>
								{nonSelectableCompanies.map(client => (
									<CompanyRowAdd
										key={client.id}
										company={client}
										cantRelate
										selected={selectedClients.some(c => c.id === client.id)}
										onSelectCompany={() => dispatch(selectClient(client))}
									/>
								))}
							</Block>
						) : null}
					</Block>
					{showEmptyState ? <EmptyState classes={classes} mainQuery={mainQuery} link={link} /> : null}
				</Block>
			);
		}
		case TABS.SELECTED_COMPANIES: {
			let subQuery: string | undefined;
			const mainQuery = t('default.noResults');

			//no selected clients or branches
			const noneSelected = !selectedClients.length && !selectedBranches.length;

			//no selected clients or branches matches the search query
			const noSearchMatches =
				numberOfSelectedCompanies && !filteredSelectedClients.length && !filteredBranchOffices.length;

			if (noneSelected) {
				subQuery = t('account.relations.noResults');
			} else if (noSearchMatches) {
				subQuery = t('account.relations.noSearchMatches');
			}

			const showEmptyState = noSearchMatches || noneSelected;

			return (
				<Block>
					<BranchOffices
						hasPendingPurchase={pendingPurchase}
						unknowns={selectedBranches}
						deselectAll={() => dispatch(deselectAll())}
						onChange={handleBranchOfficesChangeWrapper}
					/>
					{filteredSelectedClients.map(client => {
						// Check if this client is already in selectedBranches
						const isInSelectedBranches = selectedBranches.some(
							item => 'matchInUpsales' in item && item.matchInUpsales?.id === client.id
						);

						// Only render if not already in selectedBranches (UI check to avoid printing duplicates)
						if (!isInSelectedBranches) {
							return (
								<Block key={client.id} backgroundColor="grey-1">
									<CompanyRowAdd
										key={client.id}
										company={client}
										selected={filteredSelectedClients.some(c => c.id === client.id)}
										onSelectCompany={() => dispatch(selectClient(client))}
									/>
								</Block>
							);
						}
						return null;
					})}
					{showEmptyState ? <EmptyState classes={classes} mainQuery={mainQuery} subQuery={subQuery} /> : null}
				</Block>
			);
		}
	}
};

export default Tabs;
